import * as React from "react";
import { cn } from "@/lib/utils";

// Base premium skeleton with shimmer effect
const SkeletonPremium = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    variant?: "default" | "shimmer" | "glow";
    delay?: number;
  }
>(({ className, variant = "shimmer", delay = 0, style, ...props }, ref) => {
  const baseStyles = "rounded-md bg-muted";
  
  const variantStyles = {
    default: "animate-pulse",
    shimmer: "skeleton-shimmer",
    glow: "skeleton-glow",
  };

  return (
    <div
      ref={ref}
      className={cn(baseStyles, variantStyles[variant], className)}
      style={{ 
        ...style, 
        animationDelay: delay ? `${delay}ms` : undefined 
      }}
      {...props}
    />
  );
});
SkeletonPremium.displayName = "SkeletonPremium";

// Stats card skeleton for dashboard
const SkeletonStatsCard = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { delay?: number }
>(({ className, delay = 0, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "rounded-xl border border-border/30 bg-card p-6 skeleton-premium",
      className
    )}
    style={{ animationDelay: `${delay}ms` }}
    {...props}
  >
    <div className="flex items-center justify-between pb-2">
      <SkeletonPremium className="h-4 w-24" delay={delay} />
      <SkeletonPremium className="h-9 w-9 rounded-lg" delay={delay + 50} />
    </div>
    <div className="space-y-2 pt-2">
      <SkeletonPremium className="h-8 w-20" delay={delay + 100} />
      <SkeletonPremium className="h-3 w-28" delay={delay + 150} />
    </div>
  </div>
));
SkeletonStatsCard.displayName = "SkeletonStatsCard";

// Contact/Company card skeleton
const SkeletonContactCard = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { delay?: number }
>(({ className, delay = 0, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "rounded-xl border border-border/30 bg-card p-4 skeleton-premium",
      className
    )}
    style={{ animationDelay: `${delay}ms` }}
    {...props}
  >
    <div className="flex items-start justify-between mb-3">
      <div className="space-y-2 flex-1">
        <SkeletonPremium className="h-5 w-32" delay={delay} />
        <SkeletonPremium className="h-3 w-24" delay={delay + 50} />
      </div>
      <SkeletonPremium className="h-5 w-16 rounded-full" delay={delay + 100} />
    </div>
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <SkeletonPremium className="h-4 w-4 rounded-full" delay={delay + 150} />
        <SkeletonPremium className="h-3 w-36" delay={delay + 150} />
      </div>
      <div className="flex items-center gap-2">
        <SkeletonPremium className="h-4 w-4 rounded-full" delay={delay + 200} />
        <SkeletonPremium className="h-3 w-28" delay={delay + 200} />
      </div>
      <div className="flex items-center gap-2">
        <SkeletonPremium className="h-4 w-4 rounded-full" delay={delay + 250} />
        <SkeletonPremium className="h-3 w-20" delay={delay + 250} />
      </div>
    </div>
  </div>
));
SkeletonContactCard.displayName = "SkeletonContactCard";

// Company card skeleton (slightly different layout)
const SkeletonCompanyCard = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { delay?: number }
>(({ className, delay = 0, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "rounded-xl border border-border/30 bg-card p-4 skeleton-premium",
      className
    )}
    style={{ animationDelay: `${delay}ms` }}
    {...props}
  >
    <div className="flex items-start gap-3 mb-3">
      <SkeletonPremium className="h-10 w-10 rounded-lg flex-shrink-0" delay={delay} />
      <div className="space-y-2 flex-1">
        <SkeletonPremium className="h-5 w-36" delay={delay + 50} />
        <SkeletonPremium className="h-3 w-20" delay={delay + 100} />
      </div>
    </div>
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <SkeletonPremium className="h-4 w-4 rounded-full" delay={delay + 150} />
        <SkeletonPremium className="h-3 w-40" delay={delay + 150} />
      </div>
      <div className="flex items-center gap-2">
        <SkeletonPremium className="h-4 w-4 rounded-full" delay={delay + 200} />
        <SkeletonPremium className="h-3 w-28" delay={delay + 200} />
      </div>
    </div>
  </div>
));
SkeletonCompanyCard.displayName = "SkeletonCompanyCard";

// Deal card skeleton (compact for pipeline)
const SkeletonDealCard = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { delay?: number }
>(({ className, delay = 0, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "rounded-lg border border-border/30 bg-card p-3 skeleton-premium",
      className
    )}
    style={{ animationDelay: `${delay}ms` }}
    {...props}
  >
    <div className="flex items-center justify-between mb-2">
      <SkeletonPremium className="h-4 w-28" delay={delay} />
      <SkeletonPremium className="h-5 w-16" delay={delay + 50} />
    </div>
    <div className="space-y-2">
      <SkeletonPremium className="h-3 w-20" delay={delay + 100} />
      <SkeletonPremium className="h-2 w-full rounded-full" delay={delay + 150} />
    </div>
  </div>
));
SkeletonDealCard.displayName = "SkeletonDealCard";

// Pipeline column skeleton
interface SkeletonPipelineColumnProps extends React.HTMLAttributes<HTMLDivElement> {
  delay?: number;
  cardCount?: number;
  colorClass?: string;
}

const SkeletonPipelineColumn = React.forwardRef<
  HTMLDivElement,
  SkeletonPipelineColumnProps
>(({ className, delay = 0, cardCount = 3, colorClass = "border-t-muted-foreground/30", ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "w-72 flex-shrink-0 bg-muted/30 rounded-lg border-t-4",
      colorClass,
      className
    )}
    style={{ animationDelay: `${delay}ms` }}
    {...props}
  >
    <div className="p-3 border-b">
      <div className="flex items-center justify-between">
        <SkeletonPremium className="h-5 w-20" delay={delay} />
        <SkeletonPremium className="h-5 w-6 rounded-full" delay={delay + 50} />
      </div>
      <SkeletonPremium className="h-3 w-16 mt-2" delay={delay + 100} />
    </div>
    <div className="p-2 space-y-2">
      {Array.from({ length: cardCount }).map((_, i) => (
        <SkeletonDealCard key={i} delay={delay + 150 + (i * 100)} />
      ))}
    </div>
  </div>
));
SkeletonPipelineColumn.displayName = "SkeletonPipelineColumn";

// Activity item skeleton
const SkeletonActivityItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { delay?: number }
>(({ className, delay = 0, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex gap-4", className)}
    style={{ animationDelay: `${delay}ms` }}
    {...props}
  >
    <SkeletonPremium className="h-8 w-8 rounded-full flex-shrink-0 skeleton-glow" delay={delay} />
    <div className="flex-1 space-y-2">
      <div className="flex items-center gap-2">
        <SkeletonPremium className="h-4 w-32" delay={delay + 50} />
        <SkeletonPremium className="h-3 w-24" delay={delay + 100} />
      </div>
      <SkeletonPremium className="h-12 w-full rounded-lg" delay={delay + 150} />
    </div>
  </div>
));
SkeletonActivityItem.displayName = "SkeletonActivityItem";

// Task card skeleton
const SkeletonTaskCard = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { delay?: number }
>(({ className, delay = 0, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex items-center gap-3 p-3 rounded-lg border border-border/30 bg-card skeleton-premium",
      className
    )}
    style={{ animationDelay: `${delay}ms` }}
    {...props}
  >
    <SkeletonPremium className="h-4 w-4 rounded" delay={delay} />
    <SkeletonPremium className="h-4 w-4 rounded" delay={delay + 50} />
    <div className="flex-1 space-y-1">
      <SkeletonPremium className="h-4 w-40" delay={delay + 100} />
      <SkeletonPremium className="h-3 w-24" delay={delay + 150} />
    </div>
    <SkeletonPremium className="h-5 w-16 rounded-full" delay={delay + 200} />
  </div>
));
SkeletonTaskCard.displayName = "SkeletonTaskCard";

// Blog card skeleton
const SkeletonBlogCard = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { delay?: number }
>(({ className, delay = 0, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "rounded-xl border border-border/30 bg-card overflow-hidden skeleton-premium",
      className
    )}
    style={{ animationDelay: `${delay}ms` }}
    {...props}
  >
    <SkeletonPremium className="h-48 w-full rounded-none" delay={delay} />
    <div className="p-5 space-y-3">
      <div className="flex items-center gap-2">
        <SkeletonPremium className="h-5 w-20 rounded-full" delay={delay + 50} />
        <SkeletonPremium className="h-3 w-24" delay={delay + 100} />
      </div>
      <SkeletonPremium className="h-6 w-full" delay={delay + 150} />
      <SkeletonPremium className="h-4 w-4/5" delay={delay + 200} />
      <div className="flex items-center gap-3 pt-2">
        <SkeletonPremium className="h-8 w-8 rounded-full" delay={delay + 250} />
        <SkeletonPremium className="h-3 w-28" delay={delay + 300} />
      </div>
    </div>
  </div>
));
SkeletonBlogCard.displayName = "SkeletonBlogCard";

// Grid wrapper with staggered animations
interface SkeletonGridProps {
  count?: number;
  columns?: string;
  className?: string;
  children: (index: number, delay: number) => React.ReactNode;
}

const SkeletonGrid: React.FC<SkeletonGridProps> = ({ 
  className, 
  count = 6, 
  columns = "md:grid-cols-2 lg:grid-cols-3", 
  children 
}) => (
  <div className={cn("grid gap-4", columns, className)}>
    {Array.from({ length: count }).map((_, i) => children(i, i * 100))}
  </div>
);
SkeletonGrid.displayName = "SkeletonGrid";

export {
  SkeletonPremium,
  SkeletonStatsCard,
  SkeletonContactCard,
  SkeletonCompanyCard,
  SkeletonDealCard,
  SkeletonPipelineColumn,
  SkeletonActivityItem,
  SkeletonTaskCard,
  SkeletonBlogCard,
  SkeletonGrid,
};
